import React, { useState, useCallback } from "react";
import {
  Box,
  Text,
  VStack,
  Spinner,
  Tabs,
  TabPanels,
  TabPanel,
  Flex,
  useToast,
} from "@chakra-ui/react";
import { useJsApiLoader } from "@react-google-maps/api";
import { HikeSearchTrips } from "../Travel/Hikes/hikeSearchTrips";
import { RequestDelivery } from "../../deliveryModule/components/DeliveryRequestForm";
import axios from "axios";
import {
  BoxIcon,
  CarFrontIcon,
  HouseIcon,
  SendIcon,
  ShoppingCartIcon,
} from "lucide-react";
import ProductPage from "../Products/productPage";
import Cars from "../Travel/Cars/Cars";
import Accommodations from "../Travel/Accomodations/Accommodations";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import HomeTabsCarousel, { HomeTabsCarouselItem } from "./HomeTabsCarousel";
import { Global } from "@emotion/react";
import GlobalSpinner from "../CustomReusables/GlobalSpinner";

const cardsBorderRadii = "8px";

type Coords = { lat: number; lng: number; accuracy?: number };

const HomePage: React.FC = () => {
  const [coords, setCoords] = useState<Coords | null>(null);
  const [address, setAddress] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [accuracyText, setAccuracyText] = useState<string>("");
  const [tabIndex, setTabIndex] = useState(0);

  const toast = useToast();
  const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY!;
  const { theme } = useCustomTheme();

  const isDark = theme.name === "dark";

  const brand = theme.brand || {
    electricBlue: "#27b7ff",
    deepSlate: "#2a2c3b",
    deepInk: "#1a1c2f",
    slate2: "#2e3143",
    softLight: "#eaebf0",
    violet: "#b05cff",
    indigo: "#6f7cff",
  };

  const gradients = theme.gradients || {
    pageBg: theme.background,
    shellBg: theme.surface,
    primaryButton: `linear-gradient(135deg, ${brand.electricBlue} 0%, #49d1ff 100%)`,
    primaryButtonHover: `linear-gradient(135deg, #1eaef3 0%, #2cc2ff 100%)`,
    secondaryButton: `linear-gradient(135deg, ${brand.indigo} 0%, ${brand.electricBlue} 100%)`,
    secondaryButtonHover: `linear-gradient(135deg, #6170ff 0%, #20b8ff 100%)`,
    tabHikesSelected: `linear-gradient(135deg, rgba(39,183,255,0.14) 0%, rgba(73,209,255,0.08) 100%)`,
    tabDeliverySelected: `linear-gradient(135deg, rgba(111,124,255,0.14) 0%, rgba(39,183,255,0.08) 100%)`,
    tabShopSelected: `linear-gradient(135deg, rgba(176,92,255,0.14) 0%, rgba(39,183,255,0.08) 100%)`,
  };

  const pageBg = gradients.pageBg || theme.background;
  const shellBg = gradients.shellBg || theme.surface;
  const cardBg = theme.surface;
  const mutedText = theme.textMuted;

  const panelShadow = theme.shadowMd;
  const softShadow = theme.shadowSm;

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: API_KEY,
    libraries: ["places"],
  });

  const reverseGeocode = useCallback(
    async (lat: number, lng: number) => {
      try {
        const res = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${API_KEY}`,
        );
        const data = await res.json();
        if (data.results && data.results[0]) {
          setAddress(data.results[0].formatted_address);
        } else {
          setAddress("Address not found");
        }
      } catch (err) {
        console.error(err);
        setAddress("Failed to fetch address");
      }
    },
    [API_KEY],
  );

  const fetchLocation = useCallback(() => {
    setLoading(true);
    if (!navigator.geolocation) {
      setAddress("Geolocation not supported");
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        setCoords({ lat: latitude, lng: longitude, accuracy });
        setAccuracyText(`Accuracy: ±${Math.round(accuracy)}m`);
        reverseGeocode(latitude, longitude);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setAddress("Unable to fetch location");
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  }, [reverseGeocode]);

  const handleSaveLocation = async () => {
    if (!coords || !address) return;

    try {
      const res = await axios.post("/api/locations", {
        name: address,
        lat: coords.lat,
        lng: coords.lng,
      });

      toast({
        title: "Location saved successfully!",
        description: res.data?.message || "Your location has been added.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setConfirmed(true);
    } catch (err) {
      console.error(err);
      toast({
        title: "Failed to save location",
        description: "Please try again later.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (!isLoaded) {
    return (
      <Box
        w="100%"
        minH="calc(100vh - 60px)"
        bg={pageBg}
        p={{ base: 2, md: 3 }}
      >
        <Flex
          minH="260px"
          justify="center"
          align="center"
          direction="column"
          gap={3}
          borderRadius={cardsBorderRadii}
          bg={shellBg}
          boxShadow={panelShadow}
          backdropFilter={isDark ? "blur(10px)" : undefined}
        >
          <GlobalSpinner fullPage />
        </Flex>
      </Box>
    );
  }

  const homeTabs: HomeTabsCarouselItem[] = [
    {
      label: "Shop",
      subtitle: "Products & checkout",
      icon: <ShoppingCartIcon size={16} color={brand.violet} />,
      iconAccent: brand.violet,
      selectedBgGradient: gradients.tabShopSelected,
      selectedBorderAccent: theme.primary,
    },
    {
      label: "Travel & Trips",
      subtitle: "Travel & routes",
      icon: <SendIcon size={16} color={brand.electricBlue} />,
      iconAccent: brand.electricBlue,
      selectedBgGradient: gradients.tabHikesSelected,
      selectedBorderAccent: brand.electricBlue,
    },
    {
      label: "Car Hire",
      subtitle: "Cars & bookings",
      icon: <CarFrontIcon size={16} color={brand.indigo} />,
      iconAccent: brand.indigo,
      selectedBgGradient: gradients.tabDeliverySelected,
      selectedBorderAccent: brand.indigo,
    },
    {
      label: "Accommodation",
      subtitle: "Stays & rooms",
      icon: <HouseIcon size={16} color={brand.violet} />,
      iconAccent: brand.violet,
      selectedBgGradient: gradients.tabShopSelected,
      selectedBorderAccent: brand.violet,
    },
    {
      label: "Delivery",
      subtitle: "Send & track",
      icon: <BoxIcon size={16} color={brand.indigo} />,
      iconAccent: brand.indigo,
      selectedBgGradient: gradients.tabDeliverySelected,
      selectedBorderAccent: theme.primary,
    },
  ];

  return (
    <Box w="100%" minH="100%" bg={pageBg}>
      <VStack w="100%" spacing={0} align="stretch">
        <Box
          w="100%"
          borderRadius={{ base: cardsBorderRadii, md: cardsBorderRadii }}
          bg={shellBg}
          boxShadow={panelShadow}
          backdropFilter={isDark ? "blur(10px)" : undefined}
          overflow="hidden"
          pt={{ base: 0, md: 3 }}
        >
          <Tabs
            variant="unstyled"
            width="100%"
            display="flex"
            flexDirection="column"
            index={tabIndex}
            onChange={(index) => setTabIndex(index)}
          >
            <HomeTabsCarousel tabs={homeTabs} />

            <TabPanels>
              <TabPanel px={{ base: 1, md: 0 }} pb={{ base: 1, md: 0 }}>
                <Box
                  minH={{ base: "480px", md: "560px" }}
                  borderRadius={{ base: "0px", md: cardsBorderRadii }}
                  bg={cardBg}
                  boxShadow={softShadow}
                  p={{ base: 2, md: 0 }}
                  overflow="hidden"
                >
                  <ProductPage
                    onOpenBookTrip={() => setTabIndex(1)}
                    onOpenDelivery={() => setTabIndex(4)}
                  />
                </Box>
              </TabPanel>

              <TabPanel px={{ base: 0, md: 0 }} pb={{ base: 0, md: 0 }}>
                <Box
                  minH={{ base: "480px", md: "560px" }}
                  borderRadius={{ base: "0px", md: cardsBorderRadii }}
                  bg={cardBg}
                  boxShadow={softShadow}
                  p={{ base: 2, md: 3 }}
                  overflow="hidden"
                >
                  <HikeSearchTrips />
                </Box>
              </TabPanel>

              <TabPanel px={{ base: 0, md: 0 }} pb={{ base: 0, md: 0 }}>
                <Box
                  minH={{ base: "480px", md: "560px" }}
                  borderRadius={{ base: "0px", md: cardsBorderRadii }}
                  bg={cardBg}
                  boxShadow={softShadow}
                  p={{ base: 2, md: 3 }}
                  overflow="hidden"
                >
                  <Cars />
                </Box>
              </TabPanel>

              <TabPanel px={{ base: 0, md: 0 }} pb={{ base: 0, md: 0 }}>
                <Box
                  minH={{ base: "480px", md: "560px" }}
                  borderRadius={{ base: "0px", md: cardsBorderRadii }}
                  bg={cardBg}
                  boxShadow={softShadow}
                  p={{ base: 2, md: 3 }}
                  overflow="hidden"
                >
                  <Accommodations />
                </Box>
              </TabPanel>

              <TabPanel px={{ base: 0, md: 0 }} pb={{ base: 0, md: 0 }}>
                <Box
                  minH={{ base: "480px", md: "560px" }}
                  borderRadius={{ base: "0px", md: cardsBorderRadii }}
                  bg={cardBg}
                  boxShadow={softShadow}
                  p={{ base: 2, md: 3 }}
                  overflow="hidden"
                >
                  <RequestDelivery />
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </VStack>
    </Box>
  );
};

export default HomePage;
